"use client"

import { ColumnDef, RowData } from "@tanstack/react-table"
import { Check, MoreHorizontal } from "lucide-react"
import { Checkbox } from "@/Components/ui/checkbox"
import { Button } from "@/Components/ui/button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/Components/ui/dropdown-menu"
import { useContext } from "react"
import { AppContext } from "@/App"
import { AcknowledgedIcon, DashboardIcon, SeenIcon, StatusReportIcon } from "@/Components/ui/Icons/TooltipIcon"
import { PageLockContext } from "@/Components/PageLockProvider"
import { StatusCell } from "@/Components/datatable/cells/status_cell"
import { RatingCell } from "@/Components/datatable/cells/rating_cell"
import { WorkPackageCell } from "@/Components/datatable/cells/workpackage_cell"
import { DateCell } from "@/Components/datatable/cells/date_cell"
import { OwnerCell } from "@/Components/datatable/cells/owner_cell"
import { BoolCell } from "@/Components/datatable/cells/bool_cell"
import { TextCell } from "@/Components/datatable/cells/text_cell"
import { RaisedByCell } from "@/Components/datatable/cells/raisedby_cell"
import { getRatingCombination } from "@/lib/utils"
import { Risk } from "@/Components/Risks/Risk"

declare module '@tanstack/react-table' {
    //allows us to define custom properties for our columns
    interface ColumnMeta<TData extends RowData, TValue> {
        filterVariant?: 'text' | 'date-range' | 'select' | 'bool-select'
    }
    interface TableMeta<TData extends RowData> {
        updateData: (rowIndex: number, columnId: string, value: unknown) => void
    }
}

export function getRiskColumns(HandleDeleteClicked: (ids: number[]) => void, HandleEditClicked: (id: number) => void): ColumnDef<Risk>[] {
    const app = useContext(AppContext);
    const lock = useContext(PageLockContext);

    const columns: ColumnDef<Risk>[] = [];

    if (app.authLevel !== 3 && !lock.viewOnly) {
        columns.push(
            {
                id: "select",
                header: ({ table }) => (
                    <Checkbox
                        id="table-select-all"
                        checked={
                            table.getIsAllPageRowsSelected() ||
                            (table.getIsSomePageRowsSelected() && "indeterminate")
                        }
                        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                        aria-label="Select all"
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        id={"select-row-" + row.id}
                        onClick={(e) => e.stopPropagation()}
                        checked={row.getIsSelected()}
                        onCheckedChange={(value) => row.toggleSelected(!!value)}
                        aria-label="Select row"
                        className="table-row-checkbox"
                    />
                ),
                enableSorting: false,
                enableHiding: false,
            });
    }

    columns.push(
        {
            id: "riskId",
            accessorKey: "riskId",
            header: "ID",
            cell: ({ row }) => {
                const riskId = row.getValue("riskId");
                return <div className="text-center">{riskId as number}</div>
            },
            filterFn: (row, columnId, filterValue) => {
                // needs to match exact number
                const value = row.getValue(columnId) as number;
                const filterNumber = typeof filterValue === "string" ? parseInt(filterValue, 10) : filterValue;
                return value === filterNumber;
            },
            size: 50,
        });

    if (app.selectedProject?.projectTypeId === 2) {

        columns.push({
            id: "workstream",
            accessorKey: "workstream",
            header: "WS",
            meta: {
                filterVariant: 'select',
            },
            cell: ({ row }) => {
                const workstream = row.getValue("workstream");
                return <div className="text-center">{workstream as string}</div>
            },
            size: 120,
        });
    }
    columns.push(
        {
            id: "workPackage",
            accessorKey: "workPackage",
            header: "WP",
            meta: {
                filterVariant: 'select',
            },
            cell: WorkPackageCell,
            size: 120,
        },
        {
            id: "risk",
            accessorKey: "risk",
            header: "Risk",
            cell: TextCell,
            size: 300,
        },
        {
            id: "raisedBy",
            accessorKey: "raisedBy",
            header: "Raised By",
            cell: RaisedByCell,
            size: 150,
        },
        {
            id: "mitigation",
            accessorKey: "mitigation",
            header: "Mitigation",
            cell: (props) => TextCell({ ...props, maxLength: 250 }),
            size: 300,
        },
        {
            id: "probability",
            accessorKey: "probability",
            header: "Probability",
            meta: {
                filterVariant: 'select',
            },
            cell: RatingCell,
            size: 100,
        },
        {
            id: "impact",
            accessorKey: "impact",
            header: "Impact",
            meta: {
                filterVariant: 'select',
            },
            cell: RatingCell,
            size: 100,
        },
        {
            id: "rating",
            accessorKey: "rating",
            header: "Rating",
            meta: {
                filterVariant: 'select',
            },
            cell: ({ row }) => {
                const probability = row.getValue("probability");
                const impact = row.getValue("impact");
                const rating = getRatingCombination(probability as "High" | "Medium" | "Low", impact as "High" | "Medium" | "Low");

                let style = "bg-chart-green";
                if (rating === "Medium") {
                    style = "bg-chart-amber";
                } else if (rating === "High") {
                    style = "bg-chart-red";
                }
                return <div className={"text-center" + " " + style + " text-white"}>{rating}</div>
            },
            size: 100,
        },
        {
            id: "status",
            accessorKey: "status",
            header: "Status",
            meta: {
                filterVariant: 'select',
            },
            cell: StatusCell,
            size: 100,
        },
        {
            id: "date",
            accessorKey: "date",
            header: "Date Raised",
            meta: {
                filterVariant: 'date-range',
            },
            cell: DateCell,
            filterFn: (row, columnId, filterValue) => {
                const value = new Date(row.getValue(columnId));
                const from = filterValue.from ? new Date(filterValue.from) : null;
                const to = filterValue.to ? new Date(filterValue.to) : null;
                if (!from || !to) return true;
                return value >= from && value <= to;
            },
            size: 110,
        },
        {
            id: "owner",
            accessorKey: "owner",
            header: "Owner",
            cell: OwnerCell,
            size: 150,
        },
        {
            id: "notes",
            accessorKey: "notes",
            header: "Notes",
            cell: (props) => TextCell({ ...props, maxLength: 500 }),
            size: 150,
        },
        {
            id: "displayOnStatusReport",
            accessorKey: "displayOnStatusReport",
            header: () => {
                return (<StatusReportIcon />)
            },
            meta: {
                filterVariant: 'bool-select',
            },
            cell: BoolCell,
            filterFn: (row, columnId, filterValue) => {
                // Custom filtering logic for the "accepted" column
                if (filterValue === "true") {
                    return row.getValue(columnId) === true; // Show only true values
                } else if (filterValue === "false") {
                    return row.getValue(columnId) === false; // Show only false values
                } else {
                    return true; // Show all values if filterValue is not specified (e.g., reset/all)
                }
            },
            size: 80,
        },
        {
            id: "displayOnDashboard",
            accessorKey: "displayOnDashboard",
            header: () => {
                return (<DashboardIcon />)
            },
            meta: {
                filterVariant: 'bool-select',
            },
            cell: BoolCell,
            filterFn: (row, columnId, filterValue) => {
                // Custom filtering logic for the "accepted" column
                if (filterValue === "true") {
                    return row.getValue(columnId) === true; // Show only true values
                } else if (filterValue === "false") {
                    return row.getValue(columnId) === false; // Show only false values
                } else {
                    return true; // Show all values if filterValue is not specified (e.g., reset/all)
                }
            },
            size: 80,
        },
        {
            id: "seen",
            accessorKey: "seen",
            header: () => {
                return (<SeenIcon />)
            },
            meta: {
                filterVariant: 'bool-select',
            },
            cell: ({ row }) => {
                const seen = row.getValue("seen");
                return <>{seen ? <Check className="m-auto" /> : ""}</>
            },
            filterFn: (row, columnId, filterValue) => {
                // Custom filtering logic for the "accepted" column
                if (filterValue === "true") {
                    return row.getValue(columnId) === true; // Show only true values
                } else if (filterValue === "false") {
                    return row.getValue(columnId) === false; // Show only false values
                } else {
                    return true; // Show all values if filterValue is not specified (e.g., reset/all)
                }
            },
            size: 80,
        },
        {
            id: "acknowledged",
            accessorKey: "acknowledged",
            header: () => {
                return (<AcknowledgedIcon />)
            },
            meta: {
                filterVariant: 'bool-select',
            },
            cell: ({ row }) => {
                const acknowledged = row.getValue("acknowledged");
                return <>{acknowledged ? <Check className="m-auto" /> : ""}</>
            },
            filterFn: (row, columnId, filterValue) => {
                // Custom filtering logic for the "accepted" column
                if (filterValue === "true") {
                    return row.getValue(columnId) === true; // Show only true values
                } else if (filterValue === "false") {
                    return row.getValue(columnId) === false; // Show only false values
                } else {
                    return true; // Show all values if filterValue is not specified (e.g., reset/all)
                }
            },
            size: 80,
        });
    if (app.authLevel !== 3 && !lock.viewOnly) {
        columns.push(
            {
                id: "actions",
                cell: ({ row }) => {
                    const Risk = row.original

                    return (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0 table-row-menu-button">
                                    <span className="sr-only">Open menu</span>
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                {app.authLevel > 3 && !app.applicationUser?.userSettings?.inlineTableEditing && (
                                    <DropdownMenuItem
                                        onClick={(e) => {e.stopPropagation(); HandleEditClicked(Risk.riskId);}}
                                    >
                                        Edit
                                    </DropdownMenuItem>
                                )}
                                {app.authLevel > 3 && (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onClick={(e) => { e.stopPropagation(); HandleDeleteClicked([Risk.riskId]) }}
                                        >
                                            Delete
                                        </DropdownMenuItem>
                                    </>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )
                },
            }
        );
    }
    return columns
}

export const columnDisplayNames: [string, string][] = [
    ["riskId", "ID"],
    ["workstream", "Workstream"],
    ["workPackage", "Work Package"],
    ["risk", "Risk"],
    ["mitigation", "Mitigation"],
    ["rating", "Rating"],
    ["status", "Status"],
    ["raisedBy", "Raised By"],
    ["probability", "Probability"],
    ["impact", "Impact"],
    ["date", "Date Raised"],
    ["owner", "Owner"],
    ["notes", "Notes"],
    ["displayOnStatusReport", "Display on Status Report"],
    ["displayOnDashboard", "Display on Dashboard"],
    ["seen", "Seen"],
    ["acknowledged", "Acknowledged"],
]